import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { toast } from "sonner";
import { translateApiError } from "@/lib/api-errors";

const BASE_URL = "/api/v1";

// Must match the keys used in AuthContext.tsx
const ACCESS_KEY = "hrms_access_token";
const REFRESH_KEY = "hrms_refresh_token";
const EXPIRES_KEY = "hrms_token_expires";

// ---------------------------------------------------------------------------
// Token helpers — kept in sync with AuthContext storage keys
// ---------------------------------------------------------------------------

export const tokenStorage = {
  getAccess: (): string | null => localStorage.getItem(ACCESS_KEY),
  getRefresh: (): string | null => localStorage.getItem(REFRESH_KEY),
  set: (access: string, refresh: string, expiresIn: number): void => {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
    localStorage.setItem(EXPIRES_KEY, String(Date.now() + expiresIn * 1000));
  },
  clear: (): void => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(EXPIRES_KEY);
  },
  /** @deprecated Use getAccess() — kept for backwards compat */
  get: (): string | null => localStorage.getItem(ACCESS_KEY),
};

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

// `withCredentials: true` is required for the httpOnly refresh-token cookie
// (issue #80) to be sent on /api/v1/auth/refresh-cookie and /api/v1/auth/logout.
// It is harmless on every other endpoint — the cookie is path-scoped to
// /api/v1/auth on the server side, so non-auth endpoints will not receive it.
const api: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30_000,
  withCredentials: true,
});

// ---------------------------------------------------------------------------
// Request interceptor — attach JWT
// ---------------------------------------------------------------------------

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = tokenStorage.getAccess();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: unknown) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Silent refresh — single-flighted so concurrent 401s share one request
// ---------------------------------------------------------------------------

// Must mirror AuthContext's feature flag — both paths need to agree on which
// refresh endpoint to call.
const USE_COOKIE_REFRESH: boolean =
  import.meta.env.VITE_USE_COOKIE_REFRESH !== "0" &&
  import.meta.env.VITE_USE_COOKIE_REFRESH !== "false";

let refreshPromise: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  try {
    let response;
    if (USE_COOKIE_REFRESH) {
      // Refresh token rides on the httpOnly cookie; body is empty.
      response = await axios.post(
        `${BASE_URL}/auth/refresh-cookie`,
        {},
        { withCredentials: true, timeout: 10_000 },
      );
    } else {
      const rt = tokenStorage.getRefresh();
      if (!rt) return null;
      response = await axios.post(
        `${BASE_URL}/auth/refresh`,
        { refresh_token: rt },
        { withCredentials: true, timeout: 10_000 },
      );
    }
    const { access_token, refresh_token, expires_in } = response.data ?? {};
    if (!access_token || !expires_in) return null;
    tokenStorage.set(access_token, refresh_token ?? "", expires_in);
    return access_token;
  } catch {
    return null;
  }
}

/** Returns the new access token, or null if refresh failed. Coalesces
 *  concurrent callers onto a single in-flight refresh. */
function getRefreshSingleton(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// ---------------------------------------------------------------------------
// Response interceptor — handle 401 with transparent refresh-and-retry
// ---------------------------------------------------------------------------

api.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  async (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const originalRequest = error.config as
        | (InternalAxiosRequestConfig & { _retry?: boolean })
        | undefined;

      if (status === 401 && originalRequest) {
        // Don't retry the refresh / logout endpoints themselves — they
        // own their own 401 semantics, and retrying would loop.
        const url = originalRequest.url ?? "";
        const isAuthFlowEndpoint =
          url.includes("/auth/refresh") ||
          url.includes("/auth/logout") ||
          url.includes("/auth/google");

        if (!isAuthFlowEndpoint && !originalRequest._retry) {
          originalRequest._retry = true;
          const newToken = await getRefreshSingleton();
          if (newToken) {
            // Re-issue the original request with the fresh token.
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
          }
        }

        // Refresh failed (or the failing request was the refresh itself)
        // — hard logout.
        tokenStorage.clear();
        const currentPath = window.location.pathname + window.location.search;
        window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
      } else if (status === 403) {
        // Suppress the toast for callers that opt out (e.g. optional UI-affordance
        // fetches where the page is still usable without the data).
        const suppress = error.config?.headers?.["X-Suppress-Error-Toast"] === "1";
        if (!suppress) {
          const detail = error.response?.data?.detail;
          toast.error(
            translateApiError(detail, "You don't have permission to perform this action."),
          );
        }
      } else if (status === 409) {
        const detail = error.response?.data?.detail;
        return Promise.reject(
          new Error(translateApiError(detail, 'Conflict — this record already exists.')),
        );
      } else if (status && status >= 500) {
        toast.error("Something went wrong. Please try again or contact support.");
      }

      // For every other axios error (400, 404, 422, etc.) — and as a
      // graceful upgrade for the cases above that still fall through —
      // attach the FastAPI `detail` onto `error.message`. This means any
      // `catch (e) { setError((e as Error).message) }` block surfaces the
      // real backend message ("Missing required fields: Aadhaar card…")
      // instead of the useless axios default ("Request failed with status
      // code 422"). The structured `error.response.data` is preserved for
      // callers that still want to inspect it directly.
      const detail = error.response?.data?.detail;
      if (detail != null) {
        const niceMessage = translateApiError(detail, error.message);
        if (niceMessage) {
          try {
            error.message = niceMessage;
          } catch {
            // .message may be a non-writable accessor in some axios
            // versions — silently fall back to the raw error in that case.
          }
        }
      }
    }
    return Promise.reject(error);
  },
);

// ---------------------------------------------------------------------------
// Typed helper wrappers (avoids calling .data everywhere in components)
// ---------------------------------------------------------------------------

export async function get<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await api.get<T>(url, config);
  return response.data;
}

export async function post<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await api.post<T>(url, data, config);
  return response.data;
}

export async function put<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await api.put<T>(url, data, config);
  return response.data;
}

export async function patch<T>(
  url: string,
  data?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await api.patch<T>(url, data, config);
  return response.data;
}

export async function del<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await api.delete<T>(url, config);
  return response.data;
}

export default api;
